import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Link,
  Typography,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import AuthToggle from "bundles/common/components/AuthToggle";
import MainAppBar from "bundles/Layouts/MainAppBar";
import MainAppBarRecruiter from "bundles/Layouts/MainAppBarRecruiter";
import Crane from "icons/crane.svg";
import { replaceCurrentCandidate } from "bundles/Candidate/constants/candidateConstants";
import { replaceCurrentRecruiter } from "bundles/Recruiter/constants/recruiterConstants.js";
import { captureUtmParams } from "bundles/common/utils/utm";
import { getStoredFeatureSessionId } from "bundles/common/utils/trackEvent";

const socialButtons = [
  {
    name: "linkedin",
    icon: (
      <svg width="22" height="22" viewBox="0 0 448 512">
        <path
          d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"
          fill="#0A66C2"
        />
      </svg>
    ),
  },
  {
    name: "facebook",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24">
        <path
          d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
          fill="#1877F2"
        />
      </svg>
    ),
  },
  {
    name: "microsoft",
    icon: (
      <svg width="22" height="22" viewBox="0 0 23 23">
        <path fill="#f25022" d="M0 0h11v11H0z" />
        <path fill="#00a4ef" d="M0 12h11v11H0z" />
        <path fill="#7fba00" d="M12 0h11v11H12z" />
        <path fill="#ffb900" d="M12 12h11v11H12z" />
      </svg>
    ),
  },
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "hsl(220, 13%, 91%)" },
    "&:hover fieldset": { borderColor: "hsl(220, 13%, 80%)" },
    "&.Mui-focused fieldset": {
      border: "2px solid hsl(153 68% 25%)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "hsl(220, 25%, 12%)",
    pl: "0.5rem",
  },
  "& .MuiInputLabel-root": {
    color: "hsl(220, 10%, 46%)",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "&.Mui-focused": {
      color: "hsl(153 68% 25%)",
    },
  },
};

const Login = ({ defaultUserType = "candidate" }) => {
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const dispatch = useDispatch();
  const location = useLocation();
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const oauth_error = query.get("oauth_error");

  const [userType, setUserType] = useState(defaultUserType);
  const [values, setValues] = useState({
    email: "",
    password: "",
    // Candidates are always remembered server-side; this box only matters for recruiters.
    remember_me: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null);

  const isCand = userType === "candidate";

  useEffect(() => {
    captureUtmParams();
  }, []);

  // Reload bfcache restores so the server can redirect signed-in candidates.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    if (defaultUserType !== "recruiter") return;
    const kicked =
      localStorage.getItem("sessionKicked") === "true" ||
      query.get("session_kicked") === "true";
    if (kicked) {
      enqueueSnackbar(
        intl.formatMessage({ id: "devise.login.session_kicked" }),
        { variant: "warning" }
      );
      localStorage.removeItem("sessionKicked");
    }
  }, []);

  useEffect(() => {
    if (defaultUserType !== "recruiter") return;
    if (currentRecruiter?.id) {
      const redir = query.get("redirectTo");
      window.location.href = redir
        ? `${window.location.origin}/${i18nLocale}${redir}`
        : `/${i18nLocale}/recruiter`;
    }
  }, [currentRecruiter]);

  const validate = (fv = values) => {
    const temp = { ...errors };
    const req = intl.formatMessage({ id: "form_validation.required" });
    if ("email" in fv) temp.email = fv.email ? "" : req;
    if ("password" in fv) temp.password = fv.password ? "" : req;
    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    validate({ [name]: value });
  };

  const handleCheckboxValue = (e) => {
    const { name, checked } = e.target;
    setValues((v) => ({ ...v, [name]: checked }));
  };

  const [slideDirection, setSlideDirection] = useState(1);

  const handleToggle = (type) => {
    setSlideDirection(type === "recruiter" ? 1 : -1);
    setUserType(type);
  };

  const handleOauthClick = (provider) => {
    setOauthLoading(provider);
    sh.get(`auth/${provider}?scope=login`, {})
      .then((res) => {
        if (res.data?.redirect_uri) window.location.replace(res.data.redirect_uri);
        else setOauthLoading(null);
      })
      .catch((e) => {
        console.error(e);
        setOauthLoading(null);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    if (!validate(values)) {
      setLoading(false);
      return;
    }

    if (isCand) {
      sh.post(`/${i18nLocale}/candidates/sign_in`, {
        authenticity_token,
        candidate: {
          email: values.email,
          password: values.password,
          remember_me: values.remember_me,
        },
        feature_session_id: getStoredFeatureSessionId(),
      })
        .then((res) => {
          setLoading(false);
          if (res.data.company_id) {
            replaceCurrentRecruiter(dispatch, authenticity_token, () => {
              window.location.href = window.location.origin;
            });
          } else {
            replaceCurrentCandidate(dispatch, authenticity_token, () => {
              // Same-origin paths only.
              const redir = query.get("redirectTo");
              const safeRedir = redir && redir.startsWith("/") && !redir.startsWith("//");
              window.location.href = safeRedir ? redir : `/${i18nLocale}/candidate`;
            });
          }
        })
        .catch((err) => {
          setLoading(false);
          if (err.response?.status === 401)
            enqueueSnackbar(
              intl.formatMessage({ id: "devise.login.unauthorized" }),
              { variant: "error" }
            );
        });
    } else {
      sh.post(`/${i18nLocale}/recruiters/sign_in`, {
        authenticity_token,
        recruiter: {
          email: values.email,
          password: values.password,
          remember_me: values.remember_me,
        },
      })
        .then((res) => {
          const lang = res.data.preferred_locale || i18nLocale;
          setLoading(false);
          const redir = query.get("redirectTo");
          window.location.href = redir
            ? `${window.location.origin}/${i18nLocale}${redir}`
            : `/${lang}/recruiter`;
        })
        .catch((err) => {
          setLoading(false);
          if (err.response?.status === 401)
            enqueueSnackbar(
              intl.formatMessage({ id: "devise.login.unauthorized" }),
              { variant: "error" }
            );
        });
    }
  };

  const pwResetUrl = isCand
    ? `/${i18nLocale}/candidates/password/new`
    : `/${i18nLocale}/recruiters/password/new`;

  const signupUrl = isCand
    ? `/${i18nLocale}/candidates/sign_up`
    : `/${i18nLocale}/recruiters/sign_up`;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "hsl(230, 100%, 99%)", overflow: "hidden" }}>
      {/* Navbar – swaps instantly with userType */}
      {isCand ? <MainAppBar /> : <MainAppBarRecruiter />}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          px: "1.5rem",
          pt: "1rem",
          pb: "1rem",
          minHeight: "calc(100vh - 68px)",
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "64rem",
            alignItems: "center",
          }}
        >
          {/* ── Form card ── */}
          <Box
            sx={{
              width: isDesktop ? "50%" : "100%",
              bgcolor: "hsl(0, 0%, 100%)",
              borderRadius: "1rem",
              boxShadow:
                "0 4px 12px -2px rgb(0 0 0 / 0.06), 0 2px 4px -2px rgb(0 0 0 / 0.04)",
              p: isDesktop ? "2.5rem" : "2rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
            }}
          >
            {/* Title */}
            <Box sx={{ position: "relative", overflow: "hidden" }}>
              <AnimatePresence initial={false} custom={slideDirection}>
                <motion.h1
                  key={userType}
                  custom={slideDirection}
                  style={{
                    fontFamily:
                      "'Segoe UI Black', 'Segoe UI', system-ui, sans-serif",
                    fontSize: isDesktop ? "1.875rem" : "1.5rem",
                    lineHeight: 1.2,
                    color: "hsl(222.2, 84%, 4.9%)",
                    margin: 0,
                  }}
                  initial={(dir) => ({ opacity: 0, x: dir * 60 })}
                  animate={{ opacity: 1, x: 0 }}
                  exit={(dir) => ({ opacity: 0, x: dir * -60, position: "absolute" })}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                  <FormattedMessage
                    id={
                      isCand
                        ? "auth.signin_candidate_title"
                        : "auth.signin_recruiter_title"
                    }
                  />
                </motion.h1>
              </AnimatePresence>
            </Box>

            {/* Toggle */}
            <AuthToggle userType={userType} onChange={handleToggle} />

            {/* Social buttons — candidate only, animated */}
            <AnimatePresence initial={false}>
              {isCand && (
                <motion.div
                  key="social"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ overflow: "hidden" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "1rem",
                      }}
                    >
                      {socialButtons.map((btn) => (
                        <Box
                          key={btn.name}
                          component="button"
                          type="button"
                          disabled={oauthLoading === btn.name}
                          onClick={() => handleOauthClick(btn.name)}
                          sx={{
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "50%",
                            border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                            bgcolor: "hsl(0, 0%, 100%)",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            opacity: oauthLoading === btn.name ? 0.5 : 1,
                            "&:hover": {
                              bgcolor: "hsla(214.3, 31.8%, 91.4%, 0.5)",
                            },
                          }}
                        >
                          {btn.icon}
                        </Box>
                      ))}
                    </Box>

                    {oauth_error && (
                      <Typography
                        sx={{
                          color: "hsl(0, 84.2%, 60.2%)",
                          fontSize: "0.875rem",
                          textAlign: "center",
                        }}
                      >
                        <FormattedMessage id={`oauth.errors.${oauth_error}`} />
                      </Typography>
                    )}

                    {/* Divider */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        py: "0.5rem",
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          height: "1px",
                          bgcolor: "hsl(214.3, 31.8%, 91.4%)",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          color: "hsl(215.4, 16.3%, 46.9%)",
                          fontFamily: "'Segoe UI', system-ui, sans-serif",
                        }}
                      >
                        <FormattedMessage id="oauth.divider" />
                      </Typography>
                      <Box
                        sx={{
                          flex: 1,
                          height: "1px",
                          bgcolor: "hsl(214.3, 31.8%, 91.4%)",
                        }}
                      />
                    </Box>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <TextField
                fullWidth
                required
                type="email"
                variant="outlined"
                name="email"
                onChange={handleInputValue}
                onBlur={handleInputValue}
                sx={inputSx}
                {...(errors.email && {
                  error: true,
                  helperText: errors.email,
                })}
                label={<FormattedMessage id="devise.signup.email" />}
              />

              <TextField
                fullWidth
                required
                type={showPassword ? "text" : "password"}
                variant="outlined"
                name="password"
                onChange={handleInputValue}
                onBlur={handleInputValue}
                sx={inputSx}
                {...(errors.password && {
                  error: true,
                  helperText: errors.password,
                })}
                label={<FormattedMessage id="devise.signup.password" />}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: "hsl(215.4, 16.3%, 46.9%)" }}
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ textAlign: "right" }}>
                <Link
                  href={pwResetUrl}
                  sx={{
                    fontSize: "0.875rem",
                    color: "hsl(153 68% 25%)",
                    textDecoration: "none",
                    fontFamily: "'Segoe UI', system-ui, sans-serif",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <FormattedMessage id="devise.forgotten_password" />
                </Link>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.remember_me}
                    onChange={handleCheckboxValue}
                    name="remember_me"
                    sx={{
                      color: "hsl(214.3, 31.8%, 91.4%)",
                      "&.Mui-checked": { color: "hsl(153 68% 25%)" },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      color: "hsl(215.4, 16.3%, 46.9%)",
                      fontFamily: "'Segoe UI', system-ui, sans-serif",
                    }}
                  >
                    <FormattedMessage id="devise.login.remember_me" />
                  </Typography>
                }
              />

              {/* Submit */}
              <Button
                fullWidth
                type="submit"
                disabled={loading}
                sx={{
                  bgcolor: "hsl(153 68% 25%)",
                  color: "white",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  px: "1.5rem",
                  py: "0.75rem",
                  borderRadius: "0.75rem",
                  textTransform: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  mt: "0.75rem",
                  "&:hover": {
                    bgcolor: "hsl(153 68% 25% / 0.9)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "hsl(153 68% 25% / 0.3)",
                    color: "hsl(0 0% 100% / 0.6)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: "white" }}
                  />
                ) : (
                  <FormattedMessage id="auth.signin_submit" />
                )}
              </Button>
            </form>

            {/* Footer links */}
            <Box>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: "hsl(215.4, 16.3%, 46.9%)",
                  fontFamily: "'Segoe UI', system-ui, sans-serif",
                }}
              >
                <FormattedMessage id="devise.not_yet_subscribed" />{" "}
                <Link
                  href={signupUrl}
                  sx={{
                    color: "hsl(153 68% 25%)",
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <FormattedMessage id="signup" />
                </Link>
              </Typography>

              {/* Recruiter-only: confirmation resend */}
              <AnimatePresence initial={false}>
                {!isCand && (
                  <motion.div
                    key="conf"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                    style={{ overflow: "hidden" }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        color: "hsl(215.4, 16.3%, 46.9%)",
                        fontFamily: "'Segoe UI', system-ui, sans-serif",
                        mt: "0.25rem",
                      }}
                    >
                      <FormattedMessage id="devise.did_not_receive_confirmation" />{" "}
                      <Link
                        href={`/${i18nLocale}/recruiters/confirmation/new`}
                        sx={{
                          color: "hsl(153 68% 25%)",
                          fontWeight: 600,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        <FormattedMessage id="click_here" />
                      </Link>
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </Box>

          {/* ── Crane illustration ── */}
          {isDesktop && (
            <Box
              sx={{
                width: "50%",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-end",
                overflow: "visible",
                position: "relative",
                zIndex: 1,
              }}
            >
              <img
                src={Crane}
                alt="Crane illustration"
                style={{
                  height: "100vh",
                  maxHeight: "880px",
                  objectFit: "contain",
                  transform: "translateX(48px) scale(0.9)",
                  transformOrigin: "top right",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
